// HRMS-DEV/src/components/common/PdfViewerModal.jsx
// In-page overlay PDF & Document Viewer modal with prominent Close button.

import React, { useState, useEffect } from "react";

export default function PdfViewerModal({ docItem, onClose }) {
  const [blobUrl, setBlobUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (!docItem) {
      setBlobUrl("");
      return;
    }

    setLoading(true);
    setLoadError(false);

    // Support File or Blob objects directly
    const fileObj = docItem.file || (docItem instanceof Blob ? docItem : null);
    if (fileObj) {
      try {
        const url = URL.createObjectURL(fileObj);
        setBlobUrl(url);
        setLoading(false);
        return () => URL.revokeObjectURL(url);
      } catch (err) {
        console.error("Failed to create blob URL from File:", err);
      }
    }

    const rawUrl = typeof docItem === "string" ? docItem.trim() : (docItem.url || "").trim();

    if (!rawUrl) {
      setLoadError(true);
      setLoading(false);
      return;
    }

    // If Base64 Data URL, convert to clean Blob URL for iframe rendering
    if (rawUrl.startsWith("data:")) {
      try {
        const parts = rawUrl.split(";base64,");
        if (parts.length === 2) {
          const contentType = parts[0].replace("data:", "") || "application/pdf";
          const byteCharacters = atob(parts[1]);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: contentType });
          const url = URL.createObjectURL(blob);
          setBlobUrl(url);
          setLoading(false);
          return () => URL.revokeObjectURL(url);
        }
      } catch (err) {
        console.error("Failed to parse Data URI for in-page preview:", err);
      }
    }

    setBlobUrl(rawUrl);
    setLoading(false);
  }, [docItem]);

  // Close on Escape key press
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  if (!docItem) return null;

  const docName = typeof docItem === "string" ? "Attached Document" : (docItem.name || "Attached PDF Bill");
  const isImage = /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(docName) || (blobUrl && blobUrl.startsWith("data:image/"));

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(15, 23, 42, 0.75)",
        backdropFilter: "blur(4px)",
        zIndex: 999999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
        boxSizing: "border-box",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#FFFFFF",
          borderRadius: "14px",
          width: "100%",
          maxWidth: "980px",
          height: "90vh",
          maxHeight: "920px",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 25px 60px rgba(0, 0, 0, 0.35)",
          border: "1px solid #E2E8F0",
        }}
      >
        {/* Header with Document Title and Close Button */}
        <div
          style={{
            padding: "14px 20px",
            borderBottom: "1px solid #E2E8F0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "#F8FAFC",
            flexShrink: 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10, overflow: "hidden" }}>
            <span style={{ fontSize: 22 }}>📄</span>
            <div style={{ overflow: "hidden" }}>
              <div
                style={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: "#164231",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                title={docName}
              >
                {docName}
              </div>
              {docItem.category && (
                <div style={{ fontSize: 12, color: "#64748B" }}>{docItem.category}</div>
              )}
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                background: "#DC2626",
                color: "#FFFFFF",
                border: "none",
                borderRadius: "8px",
                padding: "8px 18px",
                fontSize: "13px",
                fontWeight: 700,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                boxShadow: "0 2px 6px rgba(220, 38, 38, 0.25)",
              }}
              title="Close PDF Preview (Esc)"
            >
              ✕ Close
            </button>
          </div>
        </div>

        {/* Modal Body: Embedded PDF or Image Viewer */}
        <div
          style={{
            flex: 1,
            background: "#1E293B",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            overflow: "hidden",
            minHeight: 0,
          }}
        >
          {loading ? (
            <div style={{ color: "#FFFFFF", fontSize: 14 }}>Loading document preview…</div>
          ) : loadError || !blobUrl ? (
            <div style={{ color: "#FFFFFF", textAlign: "center", padding: 24 }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>⚠️</div>
              <p style={{ margin: 0, fontWeight: 600 }}>Unable to load document preview</p>
              <p style={{ fontSize: 13, color: "#94A3B8", marginTop: 4 }}>The document reference could not be resolved.</p>
            </div>
          ) : isImage ? (
            <div style={{ width: "100%", height: "100%", overflow: "auto", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
              <img
                src={blobUrl}
                alt={docName}
                style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: 6, boxShadow: "0 4px 20px rgba(0,0,0,0.3)" }}
              />
            </div>
          ) : (
            <iframe
              src={blobUrl}
              title={docName}
              width="100%"
              height="100%"
              style={{ border: "none", width: "100%", height: "100%" }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
